import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, UserPlus, Search, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { useDebounce } from '../../hooks/useDebounce';
import { buildTeamApiService } from '../../services/buildTeamApi';
import { elasticsearchService } from '../../services/elasticsearchService';
import { usernameCacheService } from '../../services/usernameCache';
import {
  TeamMemberRole,
  TeamMemberStatus,
  TEAM_MEMBER_ROLES,
  TEAM_MEMBER_STATUSES,
} from '../../types/buildTeam';
import { PlayerProfile } from '../PlayerProfile';

interface PlayerSearchHit {
  _id: string;
  _source: {
    uuid: string;
    lastKnownName: string;
    nickname: string;
    lastKnownIp: string;
  };
}

/** Normalize to hyphenated lowercase UUID for the build API */
function normalizeMinecraftUuid(raw: string): string {
  const s = raw.replace(/\s+/g, '').toLowerCase();
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(s)) return s;
  const hex = s.replace(/-/g, '');
  if (/^[0-9a-f]{32}$/.test(hex)) {
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return raw.trim();
}

function isPreciseIpSearch(query: string): boolean {
  return /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d+/.test(query);
}

export interface CreateTeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function formatStatusLabel(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function specialtiesToJsonArray(commaSeparated: string): string | undefined {
  const parts = commaSeparated
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 0) return undefined;
  return JSON.stringify(parts);
}

const LABEL_CLASS = 'text-xs font-medium text-glass-400 mb-1.5 block';

export function CreateTeamMemberModal({ isOpen, onClose, onSuccess }: CreateTeamMemberModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [playerSearchQuery, setPlayerSearchQuery] = useState('');
  const debouncedPlayerSearch = useDebounce(playerSearchQuery, 300);
  const [searchResults, setSearchResults] = useState<PlayerSearchHit[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [minecraftUuid, setMinecraftUuid] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<TeamMemberRole>('builder');
  const [status, setStatus] = useState<TeamMemberStatus>('active');
  const [specialtiesText, setSpecialtiesText] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showManualUuid, setShowManualUuid] = useState(false);
  const [manualUuidDraft, setManualUuidDraft] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setPlayerSearchQuery('');
    setSearchResults([]);
    setSearchError(null);
    setMinecraftUuid('');
    setDisplayName('');
    setRole('builder');
    setStatus('active');
    setSpecialtiesText('');
    setNotes('');
    setError(null);
    setShowManualUuid(false);
    setManualUuidDraft('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => searchInputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, [isOpen]);

  const runPlayerSearch = useCallback(async (q: string) => {
    setSearchLoading(true);
    setSearchError(null);
    try {
      const searchResultsRes = await elasticsearchService.searchPlayers(q);
      setSearchResults(searchResultsRes.hits?.hits ?? []);
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Failed to search players');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (debouncedPlayerSearch.length < 2) {
      setSearchResults([]);
      return;
    }
    void runPlayerSearch(debouncedPlayerSearch);
  }, [debouncedPlayerSearch, isOpen, runPlayerSearch]);

  const handleUsernamesUpdated = useCallback(() => {
    searchResults.forEach((result) => {
      if (!result._source.lastKnownName) {
        const newName = usernameCacheService.getName(result._source.uuid);
        if (newName) {
          elasticsearchService.updatePlayerName(result._id, newName).catch(() => {
            /* non-fatal */
          });
        }
      }
    });
  }, [searchResults]);

  useEffect(() => {
    if (searchResults.length === 0) return;
    const uuids = searchResults.map((h) => h._source.uuid);
    usernameCacheService.prefetchUsernames(uuids);
    document.addEventListener('usernamesUpdated', handleUsernamesUpdated);
    return () => document.removeEventListener('usernamesUpdated', handleUsernamesUpdated);
  }, [searchResults, handleUsernamesUpdated]);

  const selectPlayer = (hit: PlayerSearchHit) => {
    const uuid = normalizeMinecraftUuid(hit._source.uuid);
    setMinecraftUuid(uuid);
    const nameFromIndex =
      hit._source.lastKnownName?.trim() ||
      hit._source.nickname?.trim() ||
      usernameCacheService.getName(uuid) ||
      '';
    setDisplayName(nameFromIndex || uuid);
    setPlayerSearchQuery('');
    setSearchResults([]);
    setSearchError(null);
  };

  const clearPlayerSelection = () => {
    setMinecraftUuid('');
    setDisplayName('');
  };

  const applyManualUuid = () => {
    const normalized = normalizeMinecraftUuid(manualUuidDraft);
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(normalized)) {
      setError('Enter a valid Minecraft UUID (with or without hyphens)');
      return;
    }
    setError(null);
    setMinecraftUuid(normalized);
    setDisplayName((prev) => prev.trim() || normalized);
    setShowManualUuid(false);
    setManualUuidDraft('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const uuid = normalizeMinecraftUuid(minecraftUuid);
    if (!uuid || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuid)) {
      setError('Search and select a player, or use advanced manual UUID entry');
      return;
    }
    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await buildTeamApiService.createTeamMember({
        minecraft_uuid: uuid,
        display_name: displayName.trim(),
        role,
        status,
        specialties: specialtiesToJsonArray(specialtiesText),
        notes: notes.trim() || undefined,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create team member');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-member-title"
      >
        <div className="modal-header">
          <h2 id="create-member-title" className="modal-title flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-accent-300" />
            New team member
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="modal-close touch-manipulation"
            aria-label="Close"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="alert alert-danger mb-4">
            <AlertCircle />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={LABEL_CLASS}>Player *</label>
            <p className="text-xs text-glass-500 mb-2">
              Same search as elsewhere: name, UUID, or IP via the player index.
            </p>

            {minecraftUuid && (
              <div className="p-3 rounded-lg border border-accent-500/25 bg-accent-500/5 mb-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <PlayerProfile
                      uuid={minecraftUuid}
                      name={displayName}
                      nickname={undefined}
                      showUuid
                    />
                  </div>
                  <span className="badge badge-success shrink-0">
                    <Check className="w-3 h-3" />
                    Selected
                  </span>
                </div>
                <button
                  type="button"
                  onClick={clearPlayerSelection}
                  className="text-xs text-accent-300 hover:text-accent-200 hover:underline underline-offset-2"
                  disabled={isSubmitting}
                >
                  Change player
                </button>
              </div>
            )}

            {!minecraftUuid && (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
                  <input
                    ref={searchInputRef}
                    type="search"
                    value={playerSearchQuery}
                    onChange={(e) => setPlayerSearchQuery(e.target.value)}
                    className="glass-input w-full pl-9"
                    placeholder="Search by name, UUID, or IP…"
                    autoComplete="off"
                    disabled={isSubmitting}
                  />
                  {searchLoading && (
                    <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-accent-400 animate-spin" />
                  )}
                </div>
                {searchError && (
                  <p className="text-xs text-red-400 mt-1">{searchError}</p>
                )}
                {playerSearchQuery.length > 0 && playerSearchQuery.length < 2 && (
                  <p className="text-xs text-glass-500 mt-2">Enter at least 2 characters.</p>
                )}
                {searchResults.length === 0 &&
                  debouncedPlayerSearch.length >= 2 &&
                  !searchLoading &&
                  !searchError && (
                    <p className="text-xs text-glass-500 mt-2">No players found.</p>
                  )}
                {searchResults.length > 0 && (
                  <ul className="mt-2 max-h-52 overflow-y-auto rounded-lg border border-glass-200/10 divide-y divide-glass-200/10">
                    {searchResults.map((hit) => (
                      <li key={hit._id}>
                        <button
                          type="button"
                          disabled={isSubmitting}
                          onClick={() => selectPlayer(hit)}
                          className="w-full text-left p-3 flex items-center justify-between gap-3 hover:bg-glass-200/5 transition-colors"
                        >
                          <div className="min-w-0 flex-1">
                            <PlayerProfile
                              uuid={hit._source.uuid}
                              name={hit._source.lastKnownName}
                              nickname={hit._source.nickname}
                              ipAddress={
                                isPreciseIpSearch(debouncedPlayerSearch) &&
                                hit._source.lastKnownIp?.startsWith(debouncedPlayerSearch)
                                  ? hit._source.lastKnownIp
                                  : undefined
                              }
                              showUuid
                            />
                          </div>
                          <span className="text-xs text-accent-300 shrink-0">Select</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}

            <button
              type="button"
              onClick={() => setShowManualUuid((v) => !v)}
              className="mt-2 text-xs text-glass-500 hover:text-white inline-flex items-center gap-1"
            >
              {showManualUuid ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              Advanced: enter UUID manually
            </button>
            {showManualUuid && (
              <div className="mt-2 subcard p-3 space-y-2">
                <input
                  type="text"
                  value={manualUuidDraft}
                  onChange={(e) => setManualUuidDraft(e.target.value)}
                  className="glass-input w-full font-mono"
                  placeholder="UUID with or without hyphens"
                  disabled={isSubmitting}
                  aria-label="Minecraft UUID"
                />
                <button
                  type="button"
                  onClick={applyManualUuid}
                  className="glass-button-sm"
                  disabled={isSubmitting}
                >
                  Use this UUID
                </button>
              </div>
            )}
          </div>

          <div>
            <label className={LABEL_CLASS}>Display name *</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="glass-input w-full"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS}>Role</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as TeamMemberRole)}
                className="glass-select w-full"
              >
                {TEAM_MEMBER_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {formatStatusLabel(r)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={LABEL_CLASS}>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as TeamMemberStatus)}
                className="glass-select w-full"
              >
                {TEAM_MEMBER_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {formatStatusLabel(s)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={LABEL_CLASS}>Specialties</label>
            <input
              type="text"
              value={specialtiesText}
              onChange={(e) => setSpecialtiesText(e.target.value)}
              className="glass-input w-full"
              placeholder="Comma-separated, e.g. redstone, terrain, interiors"
            />
          </div>

          <div>
            <label className={LABEL_CLASS}>Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="glass-input w-full"
              rows={3}
              placeholder="Optional"
            />
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="glass-button-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader className="w-4 h-4 animate-spin" /> : null}
              Add member
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
